import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";
import { MaskHeading, Gold, EASE } from "./Motion";
import { scrollToId } from "../../lib/scroll";

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: EASE },
});

const RotatingBadge = () => (
  <div data-testid="hero-wholesome-badge" className="absolute -top-6 right-2 z-20 h-28 w-28 sm:h-36 sm:w-36 lg:-right-8">
    <div className="relative h-full w-full rounded-full bg-ns-gold shadow-[0_20px_40px_-15px_rgba(11,60,42,0.5)]">
      <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full animate-spin-slow">
        <defs>
          <path id="circ" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
        </defs>
        <text className="fill-ns-emerald" style={{ fontSize: 12, fontWeight: 700, fontFamily: "Plus Jakarta Sans" }}>
          <textPath href="#circ" textLength="270" lengthAdjust="spacing">WHOLESOME BY CHOICE ✦</textPath>
        </text>
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-ns-emerald sm:h-14 sm:w-14">
          <Leaf className="h-5 w-5 text-ns-gold sm:h-6 sm:w-6" />
        </span>
      </div>
    </div>
  </div>
);

const PriceLabel = () => (
  <motion.div
    {...fade(1.3)}
    data-testid="hero-product-label-makhana-pops"
    className="absolute bottom-8 left-3 z-20 w-[220px] rounded-2xl bg-ns-emerald p-5 text-ns-ivory shadow-[0_30px_60px_-20px_rgba(4,27,19,0.6)] sm:left-[-2rem] lg:-left-14 animate-float"
  >
    <span className="block font-serif text-4xl font-semibold italic leading-none text-ns-gold">Affordable</span>
    <span className="mt-1 block text-[11px] font-bold uppercase tracking-[0.22em] text-ns-ivory/85">with health</span>
    <div className="mt-4 h-px w-full bg-ns-gold/30" />
    <p className="mt-3 text-[13px] font-extrabold tracking-[0.24em]">MAKHANA POPS</p>
    <p className="mt-1 text-xs font-medium tracking-[0.2em] text-ns-goldlt">CREAM N’ ONION</p>
    <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.22em] text-ns-ivory/80">Coming soon</p>
  </motion.div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[520px] [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 32px 32px)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 32px 32px)" }}
          transition={{ duration: 1.6, delay: 0.3, ease: EASE }}
          className="relative aspect-[4/5] overflow-hidden rounded-t-full rounded-b-[32px] bg-ns-emerald"
          data-testid="hero-image-frame"
        >
          <motion.img
            style={{ y: imgY }}
            initial={{ scale: 1.35 }}
            animate={{ scale: 1.15 }}
            transition={{ duration: 2.2, delay: 0.3, ease: EASE }}
            src="/images/hero.jpg"
            alt="Roasted makhana with Indian spices, herbs and ghee"
            className="h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ns-night/40 via-transparent to-transparent" />
        </motion.div>
        <div className="pointer-events-none absolute -inset-3 -z-10 rounded-t-full rounded-b-[40px] border border-ns-bronze/30" />
        <PriceLabel />
      </motion.div>
      <RotatingBadge />
    </div>
  );
};

export const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-ns-ivory pb-20 pt-32 sm:pt-36 lg:pb-28 lg:pt-40 grain">
    <div className="pointer-events-none absolute -right-40 top-20 h-[520px] w-[520px] rounded-full bg-ns-gold/20 blur-[120px]" />
    <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-7">
        <motion.p {...fade(0.1)} data-testid="hero-eyebrow" className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.28em] text-ns-bronze sm:text-xs">
          <span className="h-px w-8 bg-ns-bronze" /> Independent FMCG · West Champaran, Bihar
        </motion.p>
        <MaskHeading
          as="h1"
          onLoad
          delay={0.25}
          testId="hero-headline"
          className="mt-7 font-serif text-[2.9rem] font-semibold leading-[0.98] tracking-[-0.02em] text-ns-emerald sm:text-7xl lg:text-[5.4rem] xl:text-[6.2rem]"
          lines={["Purity, Health &", <Gold key="g">Authentic Taste</Gold>, "in Every Bite."]}
        />
        <motion.p {...fade(0.9)} data-testid="hero-supporting-text" className="mt-8 max-w-xl text-base leading-relaxed text-ns-muted sm:text-lg">
          Revolutionizing Indian snacking with 100% baked superfoods, Shuddh Ghee Makhana, and premium FMCG products made for every Indian household.
        </motion.p>
        <motion.div {...fade(1.05)} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button onClick={() => scrollToId("products")} data-testid="hero-explore-products-btn" className="btn-primary group">
            Explore Products
            <span className="grid h-8 w-8 place-items-center rounded-full bg-ns-gold text-ns-emerald transition-transform duration-500 group-hover:rotate-[-45deg]">
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
          <button onClick={() => scrollToId("distributor")} data-testid="hero-apply-distributor-btn" className="btn-outline">
            Apply for Distributorship
          </button>
        </motion.div>
        <motion.dl {...fade(1.2)} className="mt-14 grid max-w-lg grid-cols-2 border-t border-ns-emerald/15 pt-6">
          <div data-testid="hero-stat-better-snacking" className="pr-6">
            <dt className="font-serif text-5xl font-semibold text-ns-emerald">01</dt>
            <dd className="mt-2 text-sm text-ns-muted">Better snacking starts here</dd>
          </div>
          <div data-testid="hero-stat-baked" className="border-l border-ns-emerald/15 pl-6">
            <dt className="font-serif text-5xl font-semibold text-ns-emerald">100%</dt>
            <dd className="mt-2 text-sm text-ns-muted">Baked, never deep-fried</dd>
          </div>
        </motion.dl>
      </div>
      <div className="lg:col-span-5">
        <HeroVisual />
      </div>
    </div>
  </section>
);
